import React, { useEffect, useState } from "react";
import { API_URL } from "src/scenes/App";
import axios from "axios";
import styled from "styled-components";
import {
    ErrorLabel, Toast, LottieLoadingIndicator,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    UserPermissionModalContainer,
    PermissionModalSection,
    PermissionModalRowLabel,
    PermissionField,
    PermissionInput
} from "src/components";
import { PermissionModalActionRow } from "src/components/PermissionModalStyles";
import MoreAboutPermissions from "src/scenes/EmployeeList/components/MoreAboutPermissions";
import RolesForm from "src/scenes/EmployeeList/components/RolesForm";
import { ROLE_STYLES, STANDARD, MULTIPLE_PERMISSIONS, ACCOUNT_OWNER } from "src/constants/roles";
import {
    FULL_ACCESS_BUSINESS_CHAT,
    MESSAGE_CLIENTS,
    MESSAGE_EMPLOYEES,
    CREATE_GROUP_CHAT,
    WEB_DASHBOARD_ACCESS
} from "src/constants/permissions";
import {
    getDisplayPermissionSelection,
    getNextPermissionSelection,
    getSavePermissionSelection
} from "src/scenes/EmployeeList/components/permissionPresentation";
import { useSavePermissions } from "src/scenes/EmployeeList/hooks";
import pluralize from "pluralize";
import { customNames } from "src/utils/helpers";
import { USER_INFORMATION_MODAL_WIDTH } from "src/scenes/EmployeeList/components/permissionModalConstants";

const FormButtons = styled(StandardModalActionButton)`
    border-radius: 7px !important;
    box-shadow: none;
    width: 100%;
    max-width: 8.75rem;
`;

function ClientRoleAndPermissionsForm(props) {
    const { showDialog, onFormSubmissionSuccessful, handleClose, token, selectedClient } = props;
    const [isDataFetchFinished, setIsDataFetchFinished] = useState(false);
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState(null);
    const [currentSelectedRole, setCurrentSelectedRole] = useState(STANDARD);
    const [currentSelectedPermissions, setCurrentSelectedPermissions] = useState([]);
    const [disableInput, setDisableInput] = useState(false);
    const [error, setError] = useState(null);

    const { isSavingPermissions, savePermissions } = useSavePermissions();

    const headers = { headers: { Authorization: "Bearer " + token } };

    const userRoleStylesForInput = ROLE_STYLES;
    const hiddenPermissionIds = [WEB_DASHBOARD_ACCESS];

    useEffect(() => {
        setCurrentSelectedPermissions(getDisplayPermissionSelection(props.selectedClient?.roleAndPermissions?.permissionIds ? JSON.parse(props.selectedClient?.roleAndPermissions?.permissionIds) : []));
    }, []);

    useEffect(() => {
        // This will check whether the 'checked' permissions matches those of any role's default permissions;
        // if it does not match, the role should automatically become "Multiple Permissions"
        let roleToSet = MULTIPLE_PERMISSIONS;
        const selected = getSavePermissionSelection(currentSelectedPermissions)
            .filter(permissionId => !hiddenPermissionIds.includes(permissionId))
            .map(Number);
        selected.sort((a, b) => a - b);

        for (let i = 0; i < roles.length; i++) {
            const arr2 = roles[i].permissionIds
                .filter(permissionId => !hiddenPermissionIds.includes(Number(permissionId)))
                .map(Number)
                .sort((a, b) => a - b);
            if (selected.length === arr2.length) {
                const status = selected.every((v, i) => v === arr2[i]);
                if (status) {
                    roleToSet = roles[i].id;
                    break;
                }
            }
        }

        setCurrentSelectedRole(roleToSet);
    }, [currentSelectedPermissions, roles]);

    const apisGet = async (url) => {
        try {
            const response = await axios.get(`${API_URL}/${url}`, headers);
            return [response.data, null];
        }
        catch (error) {
            return [null, error];
        }
    };

    const getRolesAndDefaultPermissions = async () => {
        const [data, error] = await apisGet("permissions/roles/get");
        if (!error) {
            setRoles(data.roles);
        }
        else {
            setError(error.getApiErrorDetails().message);
        }
    };

    const getPermissions = async () => {
        const apiGetPermissionForWebDashboard = apisGet("permissions/client");
        const [data, error] = await apiGetPermissionForWebDashboard;
        if (!error) {
            setPermissions(data.permissions);
            renderForm();
            toggleDisableOnButtons();
        }
        else {
            return Toast.error(error.getApiErrorDetails().message);
        }
    };

    const renderForm = () => {
        setIsDataFetchFinished(true);
    };

    const clickRole = (roleId) => {
        const item = roles.find(role => role.id === roleId);
        setCurrentSelectedRole(roleId);
        setCurrentSelectedPermissions(getDisplayPermissionSelection(item.permissionIds));
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();
        setDisableInput(true);
        toggleDisableOnButtons();
        try {
            if (props.userRole !== ACCOUNT_OWNER) {
                const permissionIdsToSave = getSavePermissionSelection(currentSelectedPermissions)
                    .filter(permissionId => !hiddenPermissionIds.includes(permissionId));
                await savePermissions(selectedClient.employeeUuid, permissionIdsToSave, headers);
            }
            onFormSubmissionSuccessful();
        } catch (error) {
            return setError(error.getApiErrorDetails().message);
        } finally {
            setDisableInput(false);
            toggleDisableOnButtons();
        }
    };

    const toggleDisableOnButtons = () => {
        const buttons = document.querySelectorAll(".form-btns");
        Array.from(buttons).forEach(button => {
            button.classList.toggle("disabled");
        });
    };

    /**
     * disable checkbox for remaining permission when FULL_ACCESS_BUSINESS_CHAT is selected */
    const disableChatPermission = (permissionId) => {
        if (disableInput) {
            return true;
        }

        const permissionsThatCanBeDisabled = [MESSAGE_CLIENTS, MESSAGE_EMPLOYEES, CREATE_GROUP_CHAT];
        if (permissionsThatCanBeDisabled.includes(permissionId)) {
            if (currentSelectedPermissions.includes(FULL_ACCESS_BUSINESS_CHAT)) {
                return true;
            }
        }

        return false;
    };

    const setPermissionIsChecked = (permissionId, isChecked) => {
        setCurrentSelectedPermissions(getNextPermissionSelection(currentSelectedPermissions, permissionId, isChecked));
    };

    const getRoleInputStyles = (key) => userRoleStylesForInput[STANDARD][key];
    const renderRoleInputText = (name) => `${name} (${pluralize.singular(customNames.clients)})`;
    const isUserRole = (roleId) => currentSelectedRole === roleId;

    useEffect(() => {
        // this will abort ongoing async calls when component unmounts
        const controller = new AbortController();
        const { signal } = controller;
        getRolesAndDefaultPermissions(signal);
        getPermissions(true, signal);

        return () => controller.abort();
    }, []);

    return (
        <React.Fragment>
            <StandardModal
                open={showDialog}
                onClose={handleClose}
                widthMode={USER_INFORMATION_MODAL_WIDTH}
                title="User Permission"
                description="User permissions are rules that control what actions a user can perform on your business app. They help protect your privacy and security by giving you control over what information or features a user can access."
                headerAside={<MoreAboutPermissions permissions={permissions} hiddenPermissionIds={hiddenPermissionIds} />}
                footer={(
                    <PermissionModalActionRow>
                        <StandardModalActionGroup $pushRight>
                            <FormButtons onClick={handleClose} className="form-btns disabled">
                                Cancel
                            </FormButtons>
                            <FormButtons $variant="primary" type="submit" onClick={handleFormSubmit} className="form-btns disabled">
                                {isSavingPermissions ? "Loading..." : "Save"}
                            </FormButtons>
                        </StandardModalActionGroup>
                    </PermissionModalActionRow>
                )}
            >
                <UserPermissionModalContainer>
                    <PermissionModalSection>
                        <PermissionModalRowLabel>User Information</PermissionModalRowLabel>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gridColumnGap: "1.1em" }}>
                            <PermissionField>
                                <label>Name</label>
                                <PermissionInput value={selectedClient.firstName} onChange={() => ""} />
                            </PermissionField>
                            <PermissionField>
                                <label>Email</label>
                                <PermissionInput style={{ paddingRight: 20 }} value={selectedClient.email} onChange={() => ""} />
                            </PermissionField>
                            <PermissionField>
                                <label>User Type</label>
                                <PermissionInput
                                    value={renderRoleInputText(getRoleInputStyles("name"))}
                                    onChange={() => ""}
                                    style={{
                                        color: getRoleInputStyles("color"),
                                        background: getRoleInputStyles("background"),
                                        border: `1px solid ${getRoleInputStyles("color")}`,
                                        fontWeight: 500
                                    }}
                                />
                            </PermissionField>
                        </div>
                    </PermissionModalSection>

                    {!isDataFetchFinished ? <LottieLoadingIndicator /> :
                        <RolesForm
                            user={props.user}
                            isClient={true}
                            roles={roles}
                            userRole={STANDARD}
                            isUserRole={isUserRole}
                            clickRole={clickRole}
                            disableInput={disableInput}
                            currentSelectedRole={currentSelectedRole}
                            permissions={permissions}
                            currentSelectedPermissions={currentSelectedPermissions}
                            setPermissionIsChecked={setPermissionIsChecked}
                            disableChatPermission={disableChatPermission}
                            hiddenPermissionIds={hiddenPermissionIds}
                        />
                    }

                    {error !== null && <ErrorLabel>{error}</ErrorLabel>}
                </UserPermissionModalContainer>
            </StandardModal>
        </React.Fragment>
    );
}

export default ClientRoleAndPermissionsForm;
